import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { isActiveBusinessTrial, isPreviewUrl } from "../utils/trial";
import { AnimatePresence, motion } from "motion/react";
import { Phone, Trash2, GripVertical, X, Eye, EyeOff, User, Mail, Calendar, MapPin, Briefcase, Folder, Upload } from "lucide-react";

export function Labeled({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 text-sm text-slate-700">
      <span className="font-medium">{label}</span>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="px-6 pt-6 flex flex-wrap items-center justify-between gap-3">
      <div data-tour="area-header">
        <h2 className="text-[1.6rem] font-extrabold leading-tight tracking-[-.025em] text-[var(--ink)]">{title}</h2>
        {subtitle && <p className="mt-[5px] text-[.85rem] text-[var(--ink-muted)]">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Card({ children, className = "", ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={
        "rounded-[12px] border border-[var(--line)] bg-white shadow-[var(--elev)] " +
        (className || "")
      }
    >
      {children}
    </div>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={
        "w-full rounded-xl bg-white/80 border border-slate-300 px-3 py-2 text-sm shadow-inner placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2F8F83] " +
        (props.className || "")
      }
    />
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={
        "w-full rounded-xl bg-white/80 border border-slate-300 px-3 py-2 text-sm shadow-inner placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2F8F83] " +
        (props.className || "")
      }
    />
  );
}

export function Btn({
  children,
  onClick,
  variant = "primary",
  className = "",
  title,
  type,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: "primary" | "secondary" | "danger";
  className?: string;
  title?: string;
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  const base =
    "inline-flex min-w-0 max-w-full items-center justify-center gap-1.5 whitespace-normal break-words text-center leading-tight rounded-[9px] text-sm font-semibold transition-all duration-150 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(0,159,181,.2)]";
  const styles =
    variant === "secondary"
      ? "bg-white text-[var(--ink-mid)] border border-[var(--line)] px-[15px] py-[9px] hover:border-[var(--teal-mid)] hover:text-[var(--teal-dark)] hover:bg-[var(--surface)]"
      : variant === "danger"
      ? "bg-[var(--red-light)] text-[#B33636] border border-[#F1C9C9] px-[15px] py-[9px] hover:brightness-95"
      : "bg-[var(--teal)] text-white border border-transparent px-[17px] py-[10px] shadow-[0_2px_6px_rgba(0,159,181,.24)] hover:brightness-[1.07]";
  return (
    <button
      type={type || "button"}
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles} ${className}`}
      title={title}
    >
      {children}
    </button>
  );
}

export function IconTrash({ className = "w-4 h-4" }: { className?: string }) {
  return <Trash2 className={className} aria-hidden="true" />;
}

export function IconChevron({ open }: { open: boolean }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      animate={{ rotate: open ? 90 : 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <path d="M9 6l6 6-6 6" />
    </motion.svg>
  );
}

export function Collapse({ open, className, children }: { open: boolean; className?: string; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            height: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
            opacity: { duration: 0.3, delay: 0.1 },
          }}
          style={{ overflow: "hidden" }}
        >
          <div className={className}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function IconGrip({ className = "w-4 h-4" }: { className?: string }) {
  return <GripVertical className={className} aria-hidden="true" />;
}

export function IconX({ className = "w-5 h-5" }: { className?: string }) {
  return <X className={className} aria-hidden="true" />;
}

export function IconEye({ className = "w-4 h-4" }: { className?: string }) {
  return <Eye className={className} aria-hidden="true" />;
}

export function IconEyeOff({ className = "w-4 h-4" }: { className?: string }) {
  return <EyeOff className={className} aria-hidden="true" />;
}

export function IconUser({ className = "w-4 h-4" }: { className?: string }) {
  return <User className={className} aria-hidden="true" />;
}

export function IconMail({ className = "w-4 h-4" }: { className?: string }) {
  return <Mail className={className} aria-hidden="true" />;
}

export function IconPhone({ className = "w-4 h-4" }: { className?: string }) {
  return <Phone className={className} aria-hidden="true" />;
}

export function IconCalendar({ className = "w-4 h-4" }: { className?: string }) {
  return <Calendar className={className} aria-hidden="true" />;
}

export function IconMapPin({ className = "w-4 h-4" }: { className?: string }) {
  return <MapPin className={className} aria-hidden="true" />;
}

export function IconBriefcase({ className = "w-4 h-4" }: { className?: string }) {
  return <Briefcase className={className} aria-hidden="true" />;
}

export function IconFolder({ className = "w-4 h-4" }: { className?: string }) {
  return <Folder className={className} aria-hidden="true" />;
}

export function IconUpload({ className = "w-4 h-4" }: { className?: string }) {
  return <Upload className={className} aria-hidden="true" />;
}

export function Badge({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-[3px] text-[.7rem] font-bold ${className}`}>
      {children}
    </span>
  );
}

export type ToastVariant = "success" | "error" | "warning" | "info";

export function Toast({ message, variant = "success" }: { message: string; variant?: ToastVariant }) {
  const icon = (() => {
    const common = { viewBox: "0 0 24 24", className: "h-4 w-4", fill: "none", stroke: "currentColor", strokeWidth: 2 } as const;
    if (variant === "success") {
      return (
        <svg {...common} aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    }
    if (variant === "error") {
      return (
        <svg {...common} aria-hidden="true">
          <path d="M18 6L6 18" />
          <path d="M6 6l12 12" />
        </svg>
      );
    }
    if (variant === "warning") {
      return (
        <svg {...common} aria-hidden="true">
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.3 3.8l-8.4 14.5A2 2 0 0 0 3.6 21h16.8a2 2 0 0 0 1.7-3.0L13.7 3.8a2 2 0 0 0-3.4 0z" />
        </svg>
      );
    }
    return (
      <svg {...common} aria-hidden="true">
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
        <path d="M21 12a9 9 0 1 1-18 0a9 9 0 0 1 18 0z" />
      </svg>
    );
  })();
  const node = (
    <div className={`toast toast-${variant}`} role="status" aria-live="polite">
      {icon}
      <span>{message}</span>
    </div>
  );
  return typeof document === "undefined" ? node : createPortal(node, document.body);
}

export type PdfModalProps = {
  name: string;
  url: string;
  onClose: () => void;
};

export function PdfModal({ name, url, onClose }: PdfModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Testphase: keine Downloads (Ansichts-Links zeigen nur Seitenbilder).
  const canDownload = !isPreviewUrl(url) && !isActiveBusinessTrial();
  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.target = "_blank";
    a.click();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="h-full w-full bg-white overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white/80 backdrop-blur">
          <div className="text-sm font-medium truncate">{name}</div>
          <div className="ml-auto flex items-center gap-2">
            {canDownload && (
              <Btn variant="secondary" onClick={download}>
                Download
              </Btn>
            )}
            <button
              className="p-2 rounded-lg hover:bg-slate-100 border border-transparent hover:border-slate-200"
              onClick={onClose}
              title="Schliessen (Esc)"
              aria-label="Schliessen"
            >
              <IconX />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-slate-50">
          <iframe title={name} src={url} className="w-full h-full" />
        </div>
      </div>
    </div>,
    document.body
  );
}
